import React, { forwardRef, useImperativeHandle } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Checkbox, Tooltip } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { isEmpty } from 'lodash';

import { BaseFormProps, EditProfileInfoProps, ErrorObject } from '@project/types';
import { OffenderIdentifierType, RoleEnum } from '@project/enums';
import { profileEditValidationSchema } from '@project/validators';
import { FormTextField } from '@project/components';

export const EditProfileInfoForm = forwardRef<
  any,
  BaseFormProps<EditProfileInfoProps> & { onMarkEmailAsValid?: () => void }
>(
  (
    { initialValues, submitForm, onSuccess, disabled, isBusiness, onFailure, onMarkEmailAsValid },
    ref,
  ) => {
    const isPortalUser = initialValues?.roles?.some((role) => role === RoleEnum.PortalUser);

    const _handleFormSubmitError = (
      error: ErrorObject,
      actions: FormikHelpers<EditProfileInfoProps>,
    ) => {
      actions.setSubmitting(false);
      const apiErrors = error.errors;
      if (!isEmpty(apiErrors)) {
        actions.setFieldError('firstName', apiErrors.firstName);
        actions.setFieldError('lastName', apiErrors.lastName);
        actions.setFieldError('email', apiErrors.email);
        actions.setFieldError('cellphone', apiErrors.cellphone);
        actions.setFieldError('whatsAppPhone', apiErrors.cellphone);
      } else {
        onFailure();
      }
    };

    const _handleSubmission = (
      formData: EditProfileInfoProps,
      actions: FormikHelpers<EditProfileInfoProps>,
    ) => {
      submitForm(formData)
        .then(() => {
          onSuccess();
        })
        .catch((error: ErrorObject) => {
          _handleFormSubmitError(error, actions);
        })
        .finally(() => actions?.setSubmitting(false));
    };

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={isBusiness || !isPortalUser ? null : profileEditValidationSchema}
        onSubmit={_handleSubmission}
        enableReinitialize
        innerRef={ref}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          handleBlur,
          handleReset,
          setFieldValue,
          errors,
          touched,
        }) => {
          const onChangeTrn = () => {
            setFieldValue(
              'identifierType',
              values.identifierType === OffenderIdentifierType.SaIdNumber
                ? OffenderIdentifierType.Trn
                : OffenderIdentifierType.SaIdNumber,
            );
          };

          useImperativeHandle(ref, () => ({ handleSubmission: handleSubmit, errors }));

          return (
            <form
              id="EditProfileForm"
              onReset={handleReset}
              onSubmit={handleSubmit}
              className="flex flex-auto flex-col"
            >
              <div className="flex flex-row rounded-md flex-1">
                <div className="flex flex-col flex-grow-1">
                  <p>Name:</p>
                  <p>Surname:</p>
                  {!isBusiness && <p>ID:</p>}
                  <p>Cell:</p>
                  <p>WhatsApp:</p>
                  <p>Email:</p>
                  <p>Comms:</p>
                </div>
                <div className="flex flex-col flex-grow-1 ml-2">
                  <FormTextField
                    variableName="firstName"
                    placeholder="First Name"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    required
                    className="h-6 w-60 shadow-none"
                    inputClassName="h-6"
                    disabled={disabled}
                  />
                  <FormTextField
                    variableName="lastName"
                    placeholder="Last Name"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    required
                    className="h-6 w-60 shadow-none"
                    inputClassName="h-6"
                    disabled={disabled}
                  />
                  {!isBusiness && (
                    <div className="flex">
                      <FormTextField
                        variableName="identifier"
                        placeholder="ID"
                        values={values}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        required
                        className="h-6 w-44 shadow-none"
                        inputClassName="h-6"
                        disabled={disabled}
                      />
                      <Checkbox
                        checked={values?.identifierType === OffenderIdentifierType.Trn}
                        onChange={onChangeTrn}
                        className="w-6 h-6"
                        disabled={disabled}
                      />
                      <span>TRN?</span>
                    </div>
                  )}
                  <FormTextField
                    variableName="cellPhoneNumber"
                    placeholder="Cellphone Number"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    required
                    className="h-6 w-60 shadow-none"
                    inputClassName="h-6 border-0"
                    disabled={disabled}
                  />
                  <FormTextField
                    variableName="whatsAppPhoneNumber"
                    placeholder="WhatsApp Phone Number"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    required
                    className="h-6 w-60 shadow-none"
                    inputClassName="h-6 border-0"
                    disabled={disabled}
                  />
                  <div className="flex items-center gap-x-1">
                    <FormTextField
                      variableName="email"
                      placeholder="Email"
                      values={values}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      required
                      className="h-6 w-60 shadow-none"
                      inputClassName={`h-6${
                        initialValues?.invalidEmail ? ' border-2 border-red-500' : ''
                      }`}
                      disabled={disabled}
                    />
                    {initialValues?.invalidEmail && onMarkEmailAsValid && (
                      <Tooltip title="Mark email valid">
                        <button
                          type="button"
                          onClick={onMarkEmailAsValid}
                          className="text-white bg-green-500 hover:bg-green-600 font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none"
                        >
                          <CheckIcon
                            style={{ fontSize: 14, stroke: 'currentColor', strokeWidth: 2 }}
                          />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  <FormTextField
                    variableName="communicationEmail"
                    placeholder="Email 2"
                    values={values}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    className="h-6 w-60 shadow-none"
                    inputClassName="h-6"
                    disabled={disabled}
                  />
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    );
  },
);
